<template>
  <div class="card" style="max-width: 750px; margin: auto">
    <div class="card-content">
      <p class="title">
        {{ $t("signUpNewTranslator") }}
      </p>
      <form ref="form" action="onSubmit">
        <b-field v-if="!user" :label="$t('title')">
          <b-select v-model="formData.title" :placeholder="$t('nA')">
            <option value="ms">
              {{ $t("ms") }}
            </option>
            <option value="mr">
              {{ $t("mr") }}
            </option>
            <option :value="null">
              {{ $t("nA") }}
            </option>
          </b-select>
        </b-field>
        <b-field v-if="!user" :label="$t('firstName')">
          <b-input v-model="formData.firstName" required />
        </b-field>
        <b-field v-if="!user" :label="$t('lastName')">
          <b-input v-model="formData.lastName" required />
        </b-field>
        <b-field v-if="!user" :label="$t('email')">
          <b-input v-model="formData.email" type="email" required />
        </b-field>
        <div v-if="!user">
          <b-field :label="$t('password')">
            <b-input v-model="formData.password" type="password" pattern=".{6,}" required />
          </b-field>
          <password v-model="formData.password" :strength-meter-only="true" />
          <p>
            <small>
              {{ $t("passwordInfo") }}
              <small
            /></small>
          </p>
          <b-field :label="$t('repeatpassword')" :type="passwordError ? 'is-danger' : ''">
            <b-input ref="repeatpassword" v-model="repeatpassword" type="password" required />
          </b-field>
        </div>
        <div v-if="!user">
          <b-field :label="$t('telephoneNumber')" />
          <b-field>
            <p class="control dial_size">
              <b-autocomplete
                v-model="formData.dialCode"
                :data="avCodesFiltered"
                open-on-focus
                field="dial_code"
                :placeholder="$t('dial_code')"
                @select="(option) => (formData.dialCode = option.dial_code)"
              >
                <template slot-scope="props">
                  <div class="media">
                    <div class="media-content">
                      <b>
                        {{ props.option.dial_code }}
                      </b>
                      <br />
                      <small>
                        <span v-if="typeof props.option.name == 'string'">{{ props.option.name }}</span>
                      </small>
                    </div>
                  </div>
                </template>
              </b-autocomplete>
            </p>
            <b-input
              v-model="formData.telephoneNumber"
              type="number"
              :placeholder="formData.mobileNumber ? $t('optional') : ''"
              :required="formData.mobileNumber ? false : true"
            />
          </b-field>
          <b-field :label="$t('mobileNumber')" />
          <b-field>
            <p class="control dial_size">
              <b-autocomplete
                v-model="formData.mobileDialCode"
                :data="avCodesFilteredMobile"
                open-on-focus
                clear-on-select
                field="dial_code"
                :placeholder="$t('dial_code')"
                @select="(option) => (formData.mobileDialCode = option.dial_code)"
              >
                <template slot-scope="props">
                  <div class="media">
                    <div class="media-content">
                      <b>
                        {{ props.option.dial_code }}
                      </b>
                      <br />
                      <small>
                        <span v-if="typeof props.option.name == 'string'">{{ props.option.name }}</span>
                      </small>
                    </div>
                  </div>
                </template>
              </b-autocomplete>
            </p>
            <b-input
              v-model="formData.mobileNumber"
              type="number"
              :placeholder="formData.telephoneNumber ? $t('optional') : ''"
              :required="formData.telephoneNumber ? false : true"
            />
          </b-field>
        </div>
        <div class="my-6" />

        <b-field :label="$t('legalStreetName')">
          <b-input v-model="formData.legalStreetName" required />
        </b-field>

        <b-field :label="$t('legalZipCode')">
          <b-input v-model="formData.legalZipCode" required />
        </b-field>

        <b-field :label="$t('legalCity')">
          <b-input v-model="formData.legalCity" required />
        </b-field>

        <b-field :label="$t('legalCountry')">
          <b-autocomplete
            v-model="countryInput"
            :data="avCountriesFiltered"
            icon="earth"
            field="name"
            :open-on-focus="true"
            :placeholder="$t('selectACountry')"
            :loading="isLoading"
            autocomplete="off"
            @select="
              (option) => {
                formData.legalCountry = '';
                option ? (formData.legalCountry = option.alpha2) : '';
              }
            "
          />
        </b-field>

        <div class="my-6" />

        <b-message style="display: none" type="is-primary">
          {{ $t("SingleTranslatorExplained") }}
        </b-message>
        <b-field style="display: none">
          <b-checkbox v-model="formData.singleTranslatorCheckbox">
            {{ $t("SingleTranslator") }}
          </b-checkbox>
        </b-field>

        <b-message class="mt-3" type="is-primary">
          {{ $t("departmentsExplained") }}
        </b-message>
        <b-dropdown v-model="formData.selectedFields" position="is-top-right" multiple aria-role="list" class="mb-3">
          <button slot="trigger" class="button is-primary" type="button">
            <span>{{ $t("specialFields") }}</span>
            <b-icon icon="menu-down" />
          </button>

          <b-dropdown-item
            v-for="(department, index) in departments"
            :key="index"
            :value="department"
            aria-role="listitem"
          >
            {{ $t(department) }}
          </b-dropdown-item>
        </b-dropdown>
        <b-taglist class="mb-4">
          <b-tag
            v-for="(department, key) in formData.selectedFields"
            :key="key"
            closable
            type="is-primary"
            size="is-medium"
            @close="formData.selectedFields.splice(key, 1)"
          >
            {{ $t(department) }}
          </b-tag>
        </b-taglist>
        <b-message class="mt-3" type="is-primary">
          {{ $t("assocMembership") }}
        </b-message>
        <b-dropdown v-model="formData.associations" position="is-top-right" multiple aria-role="list" class="mb-3">
          <button slot="trigger" class="button is-primary" type="button">
            <span>{{ $t("assocs") }}</span>
            <b-icon icon="menu-down" />
          </button>

          <b-dropdown-item v-for="(a, index) in assocs" :key="index" :value="a" aria-role="listitem">
            {{ a }}
          </b-dropdown-item>
        </b-dropdown>
        <b-taglist class="mb-4">
          <b-tag
            v-for="(a, key) in formData.associations"
            :key="key"
            closable
            type="is-primary"
            size="is-medium"
            @close="formData.associations.splice(key, 1)"
          >
            {{ a }}
          </b-tag>
        </b-taglist>
        <b-field :label="$t('languages')" />
        <b-message class="is-primary">
          {{ $t("languagesTranslatorExplained") }}
        </b-message>
        <div v-for="lang in formData.langComb" :key="lang._sortId" class="box">
          <b-field grouped group-multiline>
            <LanguagePicker
              v-model="lang.lang1"
              class="control min-width-300"
              :blacklist="[lang.lang2]"
              :change-local="false"
              :loading="false"
              :disabled="false"
            />
            <b-tooltip type="control is-primary" :label="$t(lang.mono ? 'monodirectional' : 'bidirectional')">
              <b-button
                :icon-left="lang.mono ? 'arrow-right-bold' : 'arrow-left-right-bold'"
                @click="lang.mono = !lang.mono"
              />
            </b-tooltip>
            <LanguagePicker
              v-model="lang.lang2"
              class="control min-width-300"
              :blacklist="[lang.lang1]"
              :change-local="false"
              :loading="false"
              :disabled="false"
            />
            <b-button type="is-danger" icon-left="delete" @click="removeLangCombo(lang._sortId)" />
          </b-field>
          <div class="mt-2 ml-4">
            <b-field>
              <b-switch v-model="lang.consecutive" @click.native="lang.consecutive ? (lang.simultaneous = true) : ''">
                {{ $t("consecutive") }}
              </b-switch>
            </b-field>
            <b-field>
              <b-switch v-model="lang.simultaneous" @click.native="lang.simultaneous ? (lang.consecutive = true) : ''">
                {{ $t("simultaneous") }}
              </b-switch>
            </b-field>
          </div>
        </div>
        <b-field group-multiline grouped>
          <b-button class="is-primary control" icon-left="plus" @click="addLangCombo">
            {{ $t("addLangCombo") }}
          </b-button>
        </b-field>
      </form>
      <div class="mt-5">
        <b-button type="is-primary" :disabled="isDisabled" @click.prevent="onSubmit">
          {{ $t("signUp") }}
        </b-button>
      </div>
    </div>
    <b-loading :active.sync="isLoading" :is-full-page="false" :can-cancel="false" />
  </div>
</template>

<script>
import { functions, auth } from "@/firebase";
import { VAT_COUNTRY_LIST } from "@/env";
import isoCountries from "@/iso-countries";
import isoPhoneCodes from "@/iso-phone-codes";
import Password from "vue-password-strength-meter";
import LanguagePicker from "@/components/LanguagePicker";
import { v4 } from "uuid";

export default {
  name: "RegistrationCardTranslator",
  components: {
    Password,
    LanguagePicker,
  },
  data() {
    return {
      assocs: [
        "VKD",
        "BDÜ",
        "AIIC",
        "ADÜ Nord",
        "ATICOM",
        "VVDÜ",
        "VBDÜ",
        "VGDÜ",
        "UNIVERSITAS AUSTRIA",
        "VZGDÜ",
        "ASTTI",
        "SFT",
        "AITI",
      ],
      departments: [
        "specialFieldSport",
        "specialFieldSoccer",
        "specialFieldEconomy",
        "specialFieldEnv",
        "specialFieldTech",
        "specialFieldPolitics",
        "specialFieldPharma",
        "specialFieldMedicine",
        "specialFieldMachines",
        "specialFieldIT",
        "specialFieldFinance",
        "specialFieldEnergy",
        "specialFieldDigital",
      ],
      isDisabled: true,
      isLoading: false,
      isVatCountry: true,
      isGermany: false,

      formData: {
        title: null,
        firstName: "",
        lastName: "",
        legalStreetName: "",
        legalZipCode: "",
        legalCity: "",
        legalCountry: "",
        vatId: "",
        independent: false,
        langComb: [],
        selectedFields: [],
        email: "",
        password: "",
        telephoneNumber: "",
        mobileNumber: "",
        dialCode: "+49",
        mobileDialCode: "+49",
        checkbox: false,
        associations: [],
      },
      repeatpassword: "",
      passwordError: false,
      ref: null,
      countryInput: "",
    };
  },
  computed: {
    user() {
      return this.$store.getters.getCurrentUser ? true : false;
    },
    mobileNumber() {
      if (this.formData.mobileNumber !== "") {
        return this.formData.mobileDialCode + " " + this.formData.mobileNumber.trim();
      }
      return null;
    },
    telephoneNumber() {
      if (this.formData.telephoneNumber !== "") {
        return this.formData.dialCode + " " + this.formData.telephoneNumber.trim();
      }
      return null;
    },

    avCountries() {
      return isoCountries;
    },
    avCountriesFiltered() {
      return this.avCountries.filter((option) => {
        return (
          option.name.toString().toLowerCase().indexOf(this.countryInput.toLowerCase()) >= 0 ||
          (option.alpha3 && option.alpha3.toString().toLowerCase().indexOf(this.countryInput.toLowerCase()) >= 0)
        );
      });
    },
    // returns true if vat requirement is met
    vatRequirement() {
      // no VAT required for translators
      return true;
      /*
        if (this.isVatCountry) {
          if (this.isGermany) {
            // vat id is optional in germany
            return true
          }
          if (this.formData.vatId !== '') {
            // only true if vat id is given
            return true
          }
          return false
        } else {
          if (this.formData.checkbox) {
            // check if checked
            return true
          }  
          return false
        }*/
    },
    avCodesFiltered() {
      return isoPhoneCodes.filter((option) => {
        return (
          option.name.toString().toLowerCase().indexOf(this.formData.dialCode.toLowerCase()) >= 0 ||
          (option.dial_code &&
            option.dial_code.toString().toLowerCase().indexOf(this.formData.dialCode.toLowerCase()) >= 0) ||
          (option.code && option.code.toString().toLowerCase().indexOf(this.formData.dialCode.toLowerCase()) >= 0)
        );
      });
    },
    avCodesFilteredMobile() {
      return isoPhoneCodes.filter((option) => {
        return (
          option.name.toString().toLowerCase().indexOf(this.formData.mobileDialCode.toLowerCase()) >= 0 ||
          (option.dial_code &&
            option.dial_code.toString().toLowerCase().indexOf(this.formData.mobileDialCode.toLowerCase()) >= 0) ||
          (option.code && option.code.toString().toLowerCase().indexOf(this.formData.mobileDialCode.toLowerCase()) >= 0)
        );
      });
    },
  },
  watch: {
    // this works only with html5 validation
    // keep the submit button disabled as long as the form is invalid
    formData: {
      deep: true,
      handler() {
        this.checkValidity();
      },
    },
    countryInput: {
      deep: true,
      handler(newVal) {
        if (this.avCountries.find((option) => option.name == newVal)) {
          this.formData.legalCountry = this.avCountries.find((option) => option.name == newVal).alpha2;
        }
        this.checkValidity();
      },
    },
    isVatCountry() {
      this.isDisabled = true;
      if (this.isVatCountry) {
        this.formData.vatId = "";
      } else {
        this.formData.checkbox = false;
      }
    },
    // check that both passwords are the same
    repeatpassword: function () {
      if (this.repeatpassword !== this.formData.password) {
        this.passwordError = true;
      } else {
        this.passwordError = false;
      }

      const valid = this.$refs.form.checkValidity() && !this.passwordError;
      this.isDisabled = !valid;
    },
  },
  created() {
    this.addLangCombo();
  },
  methods: {
    checkValidity() {
      if (this.formData.legalCountry.toUpperCase().length != 0) {
        this.isVatCountry = VAT_COUNTRY_LIST.includes(this.formData.legalCountry.toUpperCase());
      }
      if (this.formData.legalCountry === "de") {
        this.isGermany = true;
      } else {
        this.isGermany = false;
      }
      const valid = this.$refs.form.checkValidity() && this.vatRequirement && this.formData.legalCountry != "";
      this.formData.vatId = this.formData.vatId.replace(/\s/g, "");
      this.isDisabled = !valid;
    },
    async onSubmit() {
      if (this.isDisabled) return;

      this.isDisabled = true;
      this.isLoading = true;

      // check for referral link
      if (localStorage.ref) {
        this.ref = localStorage.ref;
      }

      // create user
      try {
        if (!this.user) {
          await auth.createUserWithEmailAndPassword(this.formData.email, this.formData.password);
          await functions.httpsCallable("AddTranslator")(this.formData);
          await functions.httpsCallable("SetUserInformation")({
            title: this.formData.title,
            firstName: this.formData.firstName,
            lastName: this.formData.lastName,
            email: this.formData.email,
            mobileNumber: this.mobileNumber,
            telephoneNumber: this.telephoneNumber,
            ref: this.ref,
          });
        } else {
          await functions.httpsCallable("AddTranslator")(this.formData);
          await functions.httpsCallable("SetUserInformation")({});
        }
        this.$buefy.toast.open({
          message: this.$t("registrationSucceed"),
          type: "is-success",
          duration: 4000,
        });

        this.isLoading = false;
        this.$emit("registrationSuccessful");
      } catch (error) {
        console.log(error);
        this.$buefy.toast.open({
          message: this.$t("registrationFailed"),
          type: "is-danger",
        });
        this.isDisabled = false;
        this.isLoading = false;
      }
    },
    addLangCombo(lang = {}) {
      this.formData.langComb.push({
        lang1: lang.lang1 || "",
        lang2: lang.lang2 || "",
        mono: typeof lang.mono === "undefined" ? true : lang.mono,
        simultaneous: typeof lang.simultaneous === "undefined" ? false : lang.simultaneous,
        consecutive: typeof lang.consecutive === "undefined" ? true : lang.consecutive,
        _sortId: v4(),
      });
    },
    removeLangCombo(key) {
      this.$delete(
        this.formData.langComb,
        this.formData.langComb.findIndex((y) => y._sortId == key),
      );
      if (this.formData.langComb.length == 0) {
        this.addLangCombo();
      }
    },
  },
};
</script>

<style scoped>
.card >>> .media-content {
  white-space: pre-wrap;
}
.dial_size >>> .input {
  max-width: 90px;
}
.dial_size >>> .dropdown-content {
  min-width: 300px;
}
/* Chrome, Safari, Edge, Opera */
.card >>> input::-webkit-outer-spin-button,
.card >>> input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
.card >>> input[type="number"] {
  -moz-appearance: textfield;
}
</style>
